import { GrowthBook } from '@growthbook/growthbook';
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins';
import trackGrowthBookExperimentDetails from './growthBookTracking';
import resolveGrowthBookClientKey from './growthbookClientKeys';

export default async function initGrowthBook(options = { clientKey: '', featureKey: '', isSplitUrlTest: false }, gbVariationCallback, heapTrackingCallback) {
	const initStarted = performance.now();
	const clientKey = resolveGrowthBookClientKey(options.clientKey);
	if (!clientKey) {
		return;
	}

	const growthbook = new GrowthBook({
		apiHost: 'https://cdn.growthbook.io',
		clientKey: clientKey,
		enableDevMode: true,
		trackingCallback: (experiment, result) => {
			const timeTakenToDecideBucketFromPageLoad = performance.now();
			// How long from initGrowthBook() start until GrowthBook picked control or a variation (ms).
			const timeTakenToDecideBucketFromGBInit = Math.round(timeTakenToDecideBucketFromPageLoad - initStarted);
			if (options.isSplitUrlTest && result.value && result.value.urlRedirect) {
				sessionStorage.setItem('growthbookVariationDetails',
					JSON.stringify({
						experiment: experiment, result: result,
						timeTakenToDecideBucketFromPageLoad: Math.round(timeTakenToDecideBucketFromPageLoad),
						timeTakenToDecideBucketFromGBInit: timeTakenToDecideBucketFromGBInit, 
						timeTakenFromPageLoadToGBScriptReady: window.modUtils.timeTakenFromPageLoadToGBScriptReady, 
						timeTakenToDownloadGBScript: window.modUtils.timeTakenToDownloadGBScript
					}));
			} else {
				trackGrowthBookExperimentDetails(
					false,
					heapTrackingCallback,
					experiment,
					result,
					options.isSplitUrlTest,
					Math.round(timeTakenToDecideBucketFromPageLoad),
					timeTakenToDecideBucketFromGBInit,
					window.modUtils.timeTakenFromPageLoadToGBScriptReady,
					window.modUtils.timeTakenToDownloadGBScript
				);
			}
		},
		onFeatureUsage: (featureKey, result) => {
			const resultValue = result?.value;

			if (resultValue) {
				const urlParams = new URLSearchParams(window.location.search);
				const variant = typeof resultValue === 'string' && resultValue.includes(' ') ? resultValue.replace(/\s+/g, '-') : resultValue;
				const variationId = variant === 'Control' ? '0' : variant.split('-')[1];
				const forcedVariations = growthbook.context?.forcedVariations;

				if (!urlParams.has(featureKey) && result?.experimentResult?.hashUsed === false) { // reload the page when forced variation chosen with plugin
					const storedVariant = sessionStorage.getItem(featureKey);
					if (!storedVariant || (storedVariant && storedVariant !== variant)) {
						sessionStorage.setItem(featureKey, variant);
						window.location.reload();
						return;
					}
					sessionStorage.setItem(featureKey, variant);
				} else if (forcedVariations && Object.keys(forcedVariations).length === 0) { // if we "clear overrides" in plugin, also refresh experience
					sessionStorage.removeItem(featureKey);
					window.location.reload();
					return;
				}

				document.body.setAttribute('data-gb-test', variant); // TO BE DEPRECATED in favor of data-gb-variation
				document.body.setAttribute('data-gb-variation', `${featureKey}-${variationId}`);

				if (gbVariationCallback && typeof gbVariationCallback === 'function') {
					gbVariationCallback(growthbook);
				}
			}
		},
		plugins: [autoAttributesPlugin()]
	});

	await growthbook.init({ streaming: true });

	if (!options.isSplitUrlTest && options.featureKey) {
		growthbook.evalFeature(options.featureKey);

		growthbook.setRenderer(() => {
			growthbook.evalFeature(options.featureKey);
		});
	}

	// Need a consistent place to store the GB instance
	if (typeof window !== 'undefined' && window.modUtils) {
		window.modUtils.growthBook = growthbook;
	}

	return growthbook;
}
